import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	ContextMenuLinkItem,
	FormattedDate,
	IDrawerDataListColumn,
	Icon,
	Id,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useEditDrawer,
	useHasPermission,
	useLaravelCrudEntity,
	useOpen,
} from "@dgs/core";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components";
import { PermissionName } from "../../../shared/types/permission";
import { ContactUpdateInquiryConflictDrawer } from "../contactUpdateInquiries/ContactUpdateInquiryConflictDrawer";
import {
	ContactUpdateInquiryCreateDrawer,
	ContactUpdateInquiryUpdateDrawer,
} from "../contactUpdateInquiries/ContactUpdateInquiryDrawer";
import { contactUpdateInquiryService } from "../shared/api/contactUpdateInquiryService";
import {
	ContactUpdateInquiryFormValues,
	ContactUpdateInquiryIndexResource,
	ContactUpdateInquirySaveRequest,
} from "../shared/types/contactUpdateInquiry";

function mapFormValuesToSaveRequest(formValues: ContactUpdateInquiryFormValues): ContactUpdateInquirySaveRequest {
	return {
		contactFilterId: formValues.contactFilter?.id || "",
		name: formValues.name,
		consentIds: (formValues.consents || []).map((consent) => consent.id),
		additionalDataFieldIds: (formValues.additionalDataFields || []).map((dataField) => dataField.id),
		startDate: formValues.startDate || "",
		dueDate: formValues.dueDate || "",
		acceptContactUpdatesAutomatically: formValues.acceptContactUpdatesAutomatically ?? false,
		includeArchivedContacts: formValues.includeArchivedContacts ?? false,
	};
}

const Warning = styled(Icon)`
	width: 20px;
	height: 20px;
	color: ${({ theme }) => theme.colors.palette.warning.main.base};
`;

export const ContactUpdateInquiryList: FC = () => {
	const { open, handleOpen, handleClose } = useOpen();
	const [conflictUpdateInquiryId, setConflictUpdateInquiryId] = useState<Id | null>(null);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const editDrawerProps = useEditDrawer();
	const crudOperations = useLaravelCrudEntity(contactUpdateInquiryService, {
		entityName: t("Contact update campaign"),
		messages: {
			create: t("Contact update campaign was successfully created."),
			update: t("Contact update campaign was successfully updated."),
			delete: t("Contact update campaign was successfully deleted."),
		},
	});
	const canCreate = useHasPermission<PermissionName>(["contactUpdateInquiry.create"]);
	const canUpdate = useHasPermission<PermissionName>(["contactUpdateInquiry.update"]);

	const dataListColumns: IDrawerDataListColumn<ContactUpdateInquiryIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
				sortable: true,
			},
			{
				heading: t("Status"),
				valueKey: "status",
				type: "value",
				render: (i) => t(i.status),
				sortable: true,
			},
			{
				heading: t("Email delivery"),
				valueKey: "startDate",
				type: "value",
				render: (i) => <FormattedDate date={i.startDate} />,
				sortable: true,
			},
			{
				heading: t("Due date"),
				valueKey: "dueDate",
				type: "value",
				render: (i) => <FormattedDate date={i.dueDate} />,
				sortable: true,
			},
			{
				heading: t("Contacts"),
				valueKey: "contactCount",
				type: "value",
			},
			{
				heading: t("Feedbacks"),
				valueKey: "feedbackCount",
				type: "value",
			},
			{
				heading: t("Conflicts"),
				valueKey: "conflictCount",
				type: "value",
				render: (inquiry) => (
					<>
						{inquiry.conflictCount > 0 && <Warning $icon="warning" />}
						{inquiry.conflictCount}
					</>
				),
			},
			{
				heading: "",
				type: "actions",
				action: (inquiry, openDrawer) => (
					<ContextMenu displayShortcutsAs="icon">
						{inquiry.status === "PLANNED" ? (
							<ContextMenuItem action={openDrawer} label={t("Edit")} title={t("Edit")} shortcut="primary" icon="edit" />
						) : (
							<ContextMenuLinkItem
								to={`${inquiry.id}`}
								label={t("Details")}
								title={t("Details")}
								icon="eye"
								shortcut="primary"
							/>
						)}
						<ContextMenuLinkItem
							to={`../contacts/active?contactFilterId=${inquiry.contactFilterId}`}
							label={t("Contacts")}
							title={t("Contacts")}
							icon="people"
						/>
						{inquiry.conflictCount > 0 && (
							<ContextMenuItem
								action={() => {
									setConflictUpdateInquiryId(inquiry.id);
									handleOpen();
								}}
								label={t("Show conflicts")}
								title={t("Show conflicts")}
								icon="alert"
							/>
						)}
						{inquiry.status === "PLANNED" && (
							<ContextMenuDeleteItem
								action={() => crudOperations.deleteEntity(inquiry.id)}
								title={t("Delete contact update campaign")}
								label={t("Delete")}
								heading={t("Delete")}
								labels={{ close: t("Close"), confirm: t("Confirm") }}
							>
								{t("Are you sure you want to delete this contact update campaign?")}
							</ContextMenuDeleteItem>
						)}
					</ContextMenu>
				),
			},
		],
		[crudOperations, handleOpen, t],
	);

	return (
		<>
			<PaginatedDrawerAutoDataList
				canCreate={canCreate}
				canUpdate={canUpdate}
				columns={dataListColumns}
				listFetchConfig={laravelPaginatedListFetchConfig(
					contactUpdateInquiryService.listKey,
					contactUpdateInquiryService.index,
				)}
				renderCreateDrawer={(drawerProps) => (
					<ContactUpdateInquiryCreateDrawer
						{...drawerProps}
						onSubmit={(formValues) => crudOperations.createEntity(mapFormValuesToSaveRequest(formValues))}
					/>
				)}
				renderUpdateDrawer={(drawerProps) => (
					<ContactUpdateInquiryUpdateDrawer
						{...drawerProps}
						onSubmit={(formValues) =>
							crudOperations.updateEntity(drawerProps.id, mapFormValuesToSaveRequest(formValues))
						}
					/>
				)}
				translations={{
					title: t("Contact update campaigns"),
					empty: t("No contact update campaigns available"),
				}}
				initialSearch={{
					search: "",
					searchFields: [{ name: "contact_update_inquiries.name", label: t("Name") }],
				}}
				onRowClick={(inquiry) =>
					inquiry.status !== "PLANNED" ? navigate(`${inquiry.id}`) : editDrawerProps.handleOpen(inquiry.id)
				}
				initialSorting={{ column: "startDate", direction: "desc" }}
			/>
			{conflictUpdateInquiryId && (
				<ContactUpdateInquiryConflictDrawer
					conflictUpdateInquiryId={conflictUpdateInquiryId}
					open={open}
					onClose={handleClose}
					onClosed={() => setConflictUpdateInquiryId(null)}
				/>
			)}
		</>
	);
};
